/** @file Validates a clip document (design 4.4, 15.6, 17.6) and returns it typed; throws with a path. */
import { ERROR_CODES } from '@zilo/pose3d-format/errors/codes';
import { throwError } from '@zilo/pose3d-std/errors/throwError';
import type { Clip } from '@zilo/pose3d-format/types/clip';
import { expectShape } from '@zilo/pose3d-format/validate/fields/expectShape';
import { failField } from '@zilo/pose3d-format/validate/fields/failField';
import { CLIP_SHAPE } from '@zilo/pose3d-format/validate/shapes/clipShape';

/** `last` is the highest allowed frame: `frames` for keys (17.6), `frames - 1` for loopStart and start. */
function checkFrame(frame: number, path: string, last: number): void {
  if (frame > last) throwError(ERROR_CODES.FRAME_OUT_OF_RANGE, { frame, last }, { path, hint: `frames are 0..${last}` });
}

function checkKeyOrder(clip: Clip): void {
  clip.keys.forEach((key, i) => {
    const path = `/keys/${i}/frame`;
    checkFrame(key.frame, path, clip.frames);
    const prev = clip.keys[i - 1];
    if (prev !== undefined && key.frame <= prev.frame) {
      throwError(ERROR_CODES.KEY_ORDER, { frame: key.frame, previous: prev.frame }, { path, hint: 'keys strictly increase by frame' });
    }
  });
}

/** mirrorHalf eases into the twin's frame-0 key, so every keyed node needs one (design 15.6). */
function checkMirrorHalf(clip: Clip): void {
  const first = clip.keys[0];
  if (first === undefined) return;
  if (first.frame !== 0) failField('/keys/0/frame', '0 (mirrorHalf clips key frame 0)', first.frame);
  clip.keys.forEach((key, i) => {
    for (const node of Object.keys(key.nodes)) {
      if (!Object.hasOwn(first.nodes, node)) {
        failField(`/keys/${i}/nodes/${node}`, 'a node keyed at frame 0', node, 'mirrorHalf clips key every keyed node at frame 0');
      }
    }
  });
}

/** Returns `doc` as a `Clip` when it is valid; otherwise throws the first error found. */
export function validateClip(doc: unknown): Clip {
  const clip = expectShape(doc, '', CLIP_SHAPE) as Clip;
  checkFrame(clip.loopStart, '/loopStart', clip.frames - 1);
  checkFrame(clip.start, '/start', clip.frames - 1);
  checkKeyOrder(clip);
  if (clip.mirrorHalf) checkMirrorHalf(clip);
  return clip;
}
